import { forwardRef } from 'react'

import { getInitials } from '@foxino/components-common'

import { Tooltip } from '../Tooltip'
import resolveTailwindClasses from '../utils/resolveTailwindClasses'
import defaultImage from './defaultImage.svg'

type SizeType = 'xs' | 's' | 'm' | 'l' | 'xl' | '2xl' | '3xl'

const sizeToStyle: Record<SizeType, string> = {
  xs: 'w-6 h-6',
  s: 'w-8 h-8',
  m: 'w-10 h-10',
  l: 'w-12 h-12',
  xl: 'w-14 h-14',
  '2xl': 'w-16 h-16',
  '3xl': 'w-20 h-20'
}

const sizeToPadding: Record<SizeType, string> = {
  xs: 'p-1',
  s: 'p-[6px]',
  m: 'p-[10px]',
  l: 'p-3',
  xl: 'p-[14px]',
  '2xl': 'p-4',
  '3xl': 'p-4'
}

const sizeToTextStyle: Record<SizeType, string> = {
  xs: 'text-xs',
  s: 'text-sm',
  m: 'text-base',
  l: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
  '3xl': 'text-3xl'
}

const sizeToMarkerStyle: Record<SizeType, string> = {
  xs: 'w-[6px] h-[6px]',
  s: 'w-[8px] h-[8px]',
  m: 'w-[10px] h-[10px]',
  l: 'w-[12px] h-[12px]',
  xl: 'w-[14px] h-[14px]',
  '2xl': 'w-[16px] h-[16px]',
  '3xl': 'w-[20px] h-[20px]'
}

export type AvatarProps = {
  url?: string
  size?: SizeType
  fullName?: string
  marked?: boolean
  showTooltip?: boolean
  tooltipLabel?: string
  side?: 'top' | 'right' | 'bottom' | 'left'
  className?: string
} & Omit<React.HTMLProps<HTMLDivElement>, 'children' | 'size'>

/**
 * @group Components
 * @category Component
 */
export const Avatar = forwardRef(
  (
    {
      className,
      url,
      onClick,
      marked,
      fullName,
      size = 's',
      disabled,
      showTooltip = true,
      tooltipLabel,
      side = 'top',
      ...restProps
    }: AvatarProps,
    ref: React.Ref<HTMLDivElement>
  ) => {
    const src = url || (defaultImage as unknown as string)
    const sizeStyle = sizeToStyle[size]
    const textStyle = sizeToTextStyle[size]
    const markerStyle = sizeToMarkerStyle[size]
    const sizeToPaddingStyle = sizeToPadding[size]

    return (
      <div className="relative flex items-center">
        <Tooltip
          providerProps={{ delayDuration: 140 }}
          color="light"
          content={fullName || tooltipLabel || ''}
          side={side}
          sideOffset={5}
          disabled={!fullName || disabled || !showTooltip}
        >
          <div>
            <div
              onClick={onClick}
              ref={ref}
              {...restProps}
              className={resolveTailwindClasses(
                'relative cursor-default overflow-hidden rounded-full border-[1.5px] border-primary-500 bg-primary-600',
                onClick && 'cursor-pointer active:border-primary-400',
                sizeStyle,
                !url && sizeToPaddingStyle,
                className
              )}
            >
              {url ? (
                <img src={src} className="object-cover" />
              ) : fullName ? (
                <p
                  className={resolveTailwindClasses(
                    textStyle,
                    'flex h-full w-full items-center justify-center font-semibold text-primary-200'
                  )}
                >
                  {getInitials(fullName)}
                </p>
              ) : (
                <img src={src} alt="Default avatar" className="bg-cover" />
              )}
            </div>
            {marked && (
              <div
                className={resolveTailwindClasses(
                  markerStyle,
                  'absolute bottom-0 right-0 rounded-full border border-white bg-success-500'
                )}
              />
            )}
          </div>
        </Tooltip>
      </div>
    )
  }
)

Avatar.displayName = 'Avatar'
